import { getContactProfile } from "../contacts/get_contact_profile.ts";
import { getOwnerProfile } from "../contacts/get_owner_profile.ts";
import type { AffinityDb } from "../database.ts";
import {
  getEventByExternalRef,
  getEventById,
} from "../events/event_metadata.ts";
import type { ContactProfileRecord } from "../lib/types/contact_profile_record.ts";
import type { EventRecord } from "../lib/types/event_record.ts";
import type { LinkDetailRecord } from "../lib/types/link_detail_record.ts";
import type { ExternalRef } from "../lib/types/social_event_input.ts";
import { getLinkDetail } from "../links/get_link_detail.ts";
import { externalRefSchema } from "./event_metadata_schema.ts";
import type { TimeInputs } from "./parse_time_input.ts";
import {
  defineAffinityTool,
  enumSchema,
  integerSchema,
  objectSchema,
  stringSchema,
} from "./tool_metadata.ts";
import { inspectAffinityItemToolName } from "./tool_names.ts";
import { toContactEntityRef, toLinkEntityRef } from "./tool_ref.ts";
import {
  type ContactLocator,
  type LinkLocator,
  resolveContactLocator,
  resolveLinkLocator,
  withToolHandling,
} from "./tool_resolvers.ts";
import { toolFailure as invalidToolInput } from "./tool_types.ts";
import type { ToolResult } from "./tool_types.ts";
import { toolFailure, toolSuccess } from "./tool_types.ts";
import { validateToolInput } from "./validate_tool_input.ts";

export type InspectAffinityItemToolInput =
  | { kind: "event"; eventId?: number; ref?: ExternalRef }
  | { kind: "owner_profile"; topLinksLimit?: number }
  | {
      kind: "contact_profile";
      contact: ContactLocator;
      recentEventsLimit?: number;
      topLinksLimit?: number;
    }
  | {
      kind: "link";
      link: LinkLocator;
      recentEventsLimit?: number;
    };

export type InspectAffinityItemToolData =
  | { kind: "event"; detail: EventRecord }
  | { kind: "owner_profile"; detail: ContactProfileRecord }
  | { kind: "contact_profile"; detail: ContactProfileRecord }
  | { kind: "link"; detail: LinkDetailRecord };

export type InspectAffinityItemToolResult =
  ToolResult<InspectAffinityItemToolData>;

function contactLocatorSchema(description: string) {
  return {
    type: "object" as const,
    properties: {
      contactId: integerSchema("Exact contact id."),
      identity: objectSchema(
        {
          type: stringSchema("Identity type."),
          value: stringSchema("Identity value."),
        },
        ["type", "value"],
        "Contact identity locator.",
      ),
    },
    description,
  };
}

function linkLocatorSchema(description: string) {
  return {
    type: "object" as const,
    properties: {
      linkId: integerSchema("Exact link id."),
      endpoints: objectSchema(
        {
          fromContactId: integerSchema("From contact id."),
          toContactId: integerSchema("To contact id."),
        },
        ["fromContactId", "toContactId"],
        "Endpoint-based link locator.",
      ),
    },
    description,
  };
}

export function inspectAffinityItemToolHandler(
  db: AffinityDb,
  rawInput: TimeInputs<InspectAffinityItemToolInput>,
): InspectAffinityItemToolResult {
  const input = structuredClone(rawInput) as InspectAffinityItemToolInput;
  const inputError = validateToolInput(
    inspectAffinityItemTool.inputSchema,
    input,
  );
  if (inputError)
    return invalidToolInput(
      "protocol",
      "invalid_input",
      "Invalid input.",
      inputError,
    );
  return withToolHandling<InspectAffinityItemToolData>(() => {
    if (input.kind === "event") {
      if ((input.eventId === undefined) === (input.ref === undefined))
        return toolFailure(
          "protocol",
          "invalid_input",
          "Invalid event target.",
          "Provide exactly one of eventId or ref.",
        );
      const detail = input.ref
        ? getEventByExternalRef(db, input.ref.source, input.ref.id)
        : getEventById(db, input.eventId as number);
      return detail
        ? toolSuccess(
            `Loaded event #${detail.id}.`,
            { kind: "event", detail },
            { entities: [{ kind: "event", id: detail.id }] },
          )
        : toolFailure(
            "domain",
            "not_found",
            "Event not found.",
            "No event matches the target.",
          );
    }
    if (input.kind === "owner_profile") {
      const detail = getOwnerProfile(
        db,
        input.topLinksLimit === undefined
          ? undefined
          : { topLinksLimit: input.topLinksLimit },
      );
      if (!detail) {
        return toolFailure(
          "domain",
          "not_found",
          "Owner profile not found.",
          "No owner contact exists yet.",
        );
      }
      return toolSuccess(
        `Loaded owner profile for ${detail.contact.name}.`,
        { kind: input.kind, detail },
        {
          entities: [toContactEntityRef(detail.contact)],
          warnings: detail.mergedInto
            ? [{ code: "partial_match", message: detail.warnings.join("; ") }]
            : undefined,
        },
      );
    }

    if (input.kind === "contact_profile") {
      const contact = resolveContactLocator(db, input.contact, "contact", true);
      if (!contact.ok) {
        return contact.result;
      }
      const detail = getContactProfile(db, contact.value.id, {
        ...(input.topLinksLimit === undefined
          ? {}
          : { topLinksLimit: input.topLinksLimit }),
        ...(input.recentEventsLimit === undefined
          ? {}
          : { recentEventsLimit: input.recentEventsLimit }),
      });
      if (!detail) {
        return toolFailure(
          "domain",
          "not_found",
          "Contact profile not found.",
          "The target contact no longer has a readable profile.",
        );
      }
      return toolSuccess(
        `Loaded contact profile for ${detail.contact.name}.`,
        { kind: input.kind, detail },
        {
          entities: [toContactEntityRef(detail.contact)],
          warnings: detail.mergedInto
            ? [{ code: "partial_match", message: detail.warnings.join("; ") }]
            : undefined,
        },
      );
    }

    const link = resolveLinkLocator(db, input.link, "link");
    if (!link.ok) {
      return link.result;
    }
    const detail = getLinkDetail(
      db,
      link.value.id,
      input.recentEventsLimit === undefined
        ? undefined
        : { recentEventsLimit: input.recentEventsLimit },
    );
    if (!detail) {
      return toolFailure(
        "domain",
        "not_found",
        "Link detail not found.",
        "The target link no longer has a readable detail record.",
      );
    }
    return toolSuccess(
      `Loaded link detail for link #${detail.link.id}.`,
      { kind: input.kind, detail },
      {
        entities: [
          toLinkEntityRef(detail.link),
          toContactEntityRef(detail.counterparty),
        ],
      },
    );
  }, "Inspection failed.");
}

export const inspectAffinityItemTool = defineAffinityTool<
  TimeInputs<InspectAffinityItemToolInput>,
  InspectAffinityItemToolResult
>({
  name: inspectAffinityItemToolName,
  description:
    "Inspect one exact affinity item such as the owner profile, a contact profile, or one link detail.",
  whenToUse:
    "Use this when you already know the exact item you want to inspect in depth.",
  whenNotToUse:
    "Do not use this for search-style discovery or dashboard-like list reviews.",
  sideEffects: "none",
  readOnly: true,
  supportsClarification: true,
  targetKinds: ["contact", "link"],
  inputDescriptions: {
    kind: "Which exact item to inspect.",
    contact: "Contact locator for contact profile inspection.",
    link: "Link locator for link detail inspection.",
    topLinksLimit: "Optional top-links cap for profile views.",
    recentEventsLimit: "Optional recent-events cap for link detail.",
  },
  outputDescription:
    "Returns one shaped detail object and dense entity references for the inspected item.",
  inputSchema: objectSchema(
    {
      kind: enumSchema("What to inspect.", [
        "event",
        "owner_profile",
        "contact_profile",
        "link",
      ]),
      eventId: integerSchema("Exact event id."),
      ref: externalRefSchema,
      contact: contactLocatorSchema(
        "Target contact. Required when kind=contact_profile. Provide contactId or identity.",
      ),
      link: linkLocatorSchema(
        "Target link. Required when kind=link. Provide linkId or endpoints.",
      ),
      topLinksLimit: integerSchema("Optional top-links cap."),
      recentEventsLimit: integerSchema("Optional recent-events cap."),
    },
    ["kind"],
    "Inspect one exact affinity item.",
  ),
  handler: inspectAffinityItemToolHandler,
});
